// W41.0 E4 — the icon component.
//
// Spec §1 E4 replaces `lucide-react` in the AppShell / cards / rows with 13
// self-drawn glyphs, and `ADR-0106` records that as a change to `DESIGN.md §14`
// (「统一线宽 1.5 的 icon library」 → 1.9, round-capped, geometric).
//
// The glyph table itself lives in `./icons.tsx`, not here: this file exports only
// a component, because `eslint --max-warnings=0` enforces
// `react-refresh/only-export-components` and mixing the two costs a full reload
// on every icon edit.

import type { SVGProps } from "react";
import { cn } from "@/lib/utils/cn";
import { GLYPHS } from "./icons";
import type { IconName } from "./icons";

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: IconName;
  /** Pixel size for both axes. Defaults to 21 — the `.ico` base in the mockup. */
  size?: number;
  /**
   * Icons that only decorate an element which already carries a text label must
   * be hidden from assistive tech. The default is `true` because that is the
   * overwhelmingly common case here (nav tabs all have visible labels), and a
   * labelled-by mistake reads as a duplicated button name.
   * Pass `false` for a standalone icon that *is* the label.
   */
  decorative?: boolean;
  /** Accessible name. Required when `decorative` is `false`. */
  title?: string;
}

export function Icon({ name, size = 21, decorative = true, title, className, ...rest }: IconProps) {
  if (!decorative && !title) {
    // A `role="img"` with no name is worse than decorative: it announces "image"
    // and then nothing. Fail loudly at dev time rather than shipping it.
    throw new Error(`Icon "${name}": non-decorative icons need a \`title\`.`);
  }
  return (
    <svg
      className={cn("icon shrink-0", className)}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={decorative ? undefined : "img"}
      aria-hidden={decorative ? true : undefined}
      focusable="false"
      {...rest}
    >
      {decorative ? null : <title>{title}</title>}
      {GLYPHS[name]}
    </svg>
  );
}
